import { GQLResponseServerError } from '@change-corgi/core/gql';
import type { Utilities, UtilityContext } from '@change-corgi/core-react/utilityContext';

import { GoneError, NotFoundError } from '#src/shared/error';

import type {
	PetitionIdFromSlugOrErrorQuery,
	PetitionIdFromSlugOrErrorQueryVariables,
	PetitionIdFromSlugQuery,
	PetitionIdFromSlugQueryVariables,
	PetitionSlugFromIdOrErrorQuery,
	PetitionSlugFromIdOrErrorQueryVariables,
	PetitionSlugFromIdQuery,
	PetitionSlugFromIdQueryVariables,
} from './petition.graphql';
import {
	PetitionIdFromSlug,
	PetitionIdFromSlugOrError,
	PetitionSlugFromId,
	PetitionSlugFromIdOrError,
} from './petition.graphql';

export async function getPetitionId(
	slug: string,
	utilities: UtilityContext | Pick<Utilities, 'gql'>,
): Promise<string | undefined> {
	try {
		const { data } = await utilities.gql.fetch<PetitionIdFromSlugQuery, PetitionIdFromSlugQueryVariables>({
			query: PetitionIdFromSlug,
			variables: { slug },
			rejectOnError: true,
		});
		return data?.petition?.id ?? undefined;
	} catch (e) {
		if (e instanceof GQLResponseServerError) {
			throw e;
		}
		return undefined;
	}
}

export async function getPetitionIdOrThrow(
	slug: string,
	utilities: UtilityContext | Pick<Utilities, 'gql'>,
): Promise<string> {
	const { data } = await utilities.gql.fetch<PetitionIdFromSlugOrErrorQuery, PetitionIdFromSlugOrErrorQueryVariables>({
		query: PetitionIdFromSlugOrError,
		variables: { slug },
		rejectOnError: true,
	});
	const result = data?.petitionBySlugOrIdOrError;
	if (result?.__typename === 'PetitionFound') return result.petition.id;
	if (result?.__typename === 'PetitionGone') throw new GoneError();
	throw new NotFoundError();
}

export async function getPetitionSlug(
	slugOrId: string,
	// Utilities support is currently necessary for this to be called by route config
	utilities: UtilityContext | Pick<Utilities, 'gql'>,
): Promise<string | undefined> {
	try {
		const { data } = await utilities.gql.fetch<PetitionSlugFromIdQuery, PetitionSlugFromIdQueryVariables>({
			query: PetitionSlugFromId,
			variables: { slugOrId },
			rejectOnError: true,
		});
		return data?.petition?.slug ?? undefined;
	} catch (e) {
		if (e instanceof GQLResponseServerError) {
			throw e;
		}
		return undefined;
	}
}

export async function getPetitionSlugOrThrow(
	slugOrId: string,
	utilities: UtilityContext | Pick<Utilities, 'gql'>,
): Promise<string> {
	const { data } = await utilities.gql.fetch<PetitionSlugFromIdOrErrorQuery, PetitionSlugFromIdOrErrorQueryVariables>({
		query: PetitionSlugFromIdOrError,
		variables: { slugOrId },
		rejectOnError: true,
	});
	const result = data?.petitionBySlugOrIdOrError;
	if (result?.__typename === 'PetitionFound') return result.petition.slug;
	if (result?.__typename === 'PetitionGone') throw new GoneError();
	throw new NotFoundError();
}
